import React from 'react';
import styles from './SearchableSelect.module.css';

export type SearchableSelectOption = {
  value: string;
  label: string;
  meta?: string;
};

type SearchableSelectBaseProps = {
  options: SearchableSelectOption[];
  placeholder?: string;
  clearOption?: SearchableSelectOption;
  inputClassName?: string;
  className?: string;
  disabled?: boolean;
  maxSuggestions?: number;
  noOptionsText?: string;
  ariaLabel?: string;
};

export type SearchableSelectProps = SearchableSelectBaseProps & (
  | {
      multiple?: false;
      value: string;
      onChange: (value: string) => void;
    }
  | {
      multiple: true;
      value: string[];
      onChange: (value: string[]) => void;
    }
);

const normalize = (value: string): string => value.trim().toLowerCase();

const mergeClassNames = (...names: Array<string | undefined | false>): string =>
  names.filter(Boolean).join(' ');

const optionMatches = (option: SearchableSelectOption, needle: string): boolean => {
  if (!needle) {
    return true;
  }

  const haystack = normalize(`${option.label} ${option.meta ?? ''}`);
  return haystack.includes(needle);
};

export const SearchableSelect: React.FC<SearchableSelectProps> = (props) => {
  const {
    options,
    placeholder,
    clearOption,
    inputClassName,
    className,
    disabled = false,
    maxSuggestions = 12,
    noOptionsText = 'No matches',
    ariaLabel,
  } = props;
  const isMultiple = props.multiple === true;
  const multipleValue = isMultiple ? props.value : null;
  const singleSelectedValue = isMultiple ? '' : props.value;
  const selectedValues = React.useMemo(
    () => (isMultiple ? (multipleValue ?? []) : (singleSelectedValue ? [singleSelectedValue] : [])),
    [isMultiple, multipleValue, singleSelectedValue],
  );
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const listboxId = React.useId();

  const selectedOption = React.useMemo(
    () => options.find((option) => option.value === singleSelectedValue) ?? null,
    [options, singleSelectedValue],
  );

  const selectedOptions = React.useMemo(() => {
    const byValue = new Map(options.map((option) => [option.value, option]));
    return selectedValues
      .map((value) => byValue.get(value) ?? { value, label: value })
      .filter((option) => option.value);
  }, [options, selectedValues]);

  const selectableOptions = React.useMemo(() => {
    const items = !isMultiple && clearOption ? [clearOption, ...options] : options;
    const seen = new Set<string>();
    const result: SearchableSelectOption[] = [];

    for (const item of items) {
      const key = `${item.value}::${normalize(item.label)}`;
      if (seen.has(key)) {
        continue;
      }

      seen.add(key);
      result.push(item);
    }

    return result;
  }, [clearOption, isMultiple, options]);

  const filteredOptions = React.useMemo(() => {
    const needle = normalize(query);
    const matched = needle
      ? selectableOptions.filter((option) => optionMatches(option, needle))
      : selectableOptions;

    const startsWith = matched.filter((option) => normalize(option.label).startsWith(needle));
    const rest = matched.filter((option) => !normalize(option.label).startsWith(needle));
    return [...startsWith, ...rest].slice(0, maxSuggestions);
  }, [maxSuggestions, query, selectableOptions]);

  React.useEffect(() => {
    setActiveIndex(filteredOptions.length > 0 ? 0 : -1);
  }, [filteredOptions]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const commitSingle = (nextValue: string) => {
    if (isMultiple) {
      return;
    }

    props.onChange(nextValue);
    setIsOpen(false);
    setQuery('');
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const toggleMultiple = (nextValue: string) => {
    if (!isMultiple || !nextValue) {
      return;
    }

    const exists = selectedValues.includes(nextValue);
    props.onChange(exists
      ? selectedValues.filter((value) => value !== nextValue)
      : [...selectedValues, nextValue]);
    setQuery('');
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const openMenu = () => {
    if (disabled) {
      return;
    }

    if (!isMultiple) {
      setQuery(selectedOption?.label ?? '');
    }
    setIsOpen(true);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }

    if (isMultiple && event.key === 'Backspace' && !query && selectedValues.length > 0) {
      event.preventDefault();
      toggleMultiple(selectedValues[selectedValues.length - 1]);
      return;
    }

    if (!isOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      openMenu();
      return;
    }

    if (!isOpen) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (filteredOptions.length === 0) {
        return;
      }

      setActiveIndex((prev) => (prev + 1) % filteredOptions.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (filteredOptions.length === 0) {
        return;
      }

      setActiveIndex((prev) => (prev <= 0 ? filteredOptions.length - 1 : prev - 1));
      return;
    }

    if (event.key === 'Enter') {
      if (activeIndex >= 0 && filteredOptions[activeIndex]) {
        event.preventDefault();
        if (isMultiple) {
          toggleMultiple(filteredOptions[activeIndex].value);
        } else {
          commitSingle(filteredOptions[activeIndex].value);
        }
      }
      return;
    }

    if (event.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
    }
  };

  const inputValue = isMultiple ? query : (isOpen ? query : (selectedOption?.label ?? ''));

  return (
    <div className={mergeClassNames(styles.root, className)} ref={wrapperRef}>
      {isMultiple ? (
        <div
          className={mergeClassNames(styles.multiControl, disabled && styles.multiControlDisabled)}
          onClick={() => {
            if (!disabled) {
              inputRef.current?.focus();
              openMenu();
            }
          }}
        >
          {selectedOptions.map((option) => (
            <span key={option.value} className={styles.chip}>
              {option.label}
              <button
                type="button"
                className={styles.chipRemove}
                aria-label={option.label}
                disabled={disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleMultiple(option.value);
                }}
              >
                ×
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            className={styles.multiInput}
            value={inputValue}
            placeholder={selectedOptions.length === 0 ? placeholder : ''}
            disabled={disabled}
            onFocus={openMenu}
            onChange={(event) => {
              setQuery(event.target.value);
              setIsOpen(true);
            }}
            onKeyDown={onKeyDown}
            role="combobox"
            aria-expanded={isOpen}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-label={ariaLabel}
          />
        </div>
      ) : (
        <input
          ref={inputRef}
          className={inputClassName}
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled}
          onFocus={openMenu}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
          }}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-label={ariaLabel}
        />
      )}
      {isOpen ? (
        <div className={styles.menu} id={listboxId} role="listbox" aria-multiselectable={isMultiple || undefined}>
          {filteredOptions.length === 0 ? (
            <div className={styles.empty}>{noOptionsText}</div>
          ) : (
            filteredOptions.map((option, index) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <button
                  key={`${option.value}-${option.label}`}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={mergeClassNames(
                    styles.option,
                    index === activeIndex && styles.optionActive,
                    isMultiple && isSelected && styles.optionSelected,
                  )}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => (isMultiple ? toggleMultiple(option.value) : commitSingle(option.value))}
                >
                  {isMultiple ? (
                    <span className={mergeClassNames(styles.check, isSelected && styles.checkOn)} aria-hidden>
                      {isSelected ? '✓' : ''}
                    </span>
                  ) : null}
                  <span className={styles.optionMain}>
                    <span className={styles.optionLabel}>{option.label}</span>
                    {option.meta ? <span className={styles.optionMeta}>{option.meta}</span> : null}
                  </span>
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
};

export default SearchableSelect;
